import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import {
    AspectRatioWrapper,
    Button,
    IconClose,
    IconArrowHead,
    ResponsiveImage,
} from '../';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ImageCarousel.module.css';

const IMAGE_CAROUSEL_OPTIONS = {
    showPlayButton: false,
    disableThumbnailScroll: true,
};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

const getFirstImageAspectRatio = (firstImage) => {
    if (!firstImage) {
        return { aspectWidth: 1, aspectHeight: 1 };
    }

    const w = firstImage?.width;
    const h = firstImage?.height;
    const hasDimensions = !!w && !!h;
    const aspectRatio = w / h;

    // We keep the fractions separated as these are given to AspectRatioWrapper
    // which expects separate width and height
    return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
        ? { aspectWidth: 2, aspectHeight: 1 }
        : hasDimensions && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
            ? { aspectWidth: 4, aspectHeight: 3 }
            : hasDimensions
                ? { aspectWidth: w, aspectHeight: h }
                : { aspectWidth: 1, aspectHeight: 1 };
};

const ImageCarousel = props => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const { intl, rootClassName, className, images, title } = props;

    // imageVariants are scaled variants.
    const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(images?.[0]);
    const items = images.map((img, i) => {
        return {
            // We will only use the image resource, but react-image-gallery
            // requires the `original` key from each item.
            original: '',
            alt: intl.formatMessage(
                { id: 'ListingImageGallery.imageAltText' },
                { title, index: i + 1, count: images.length }
            ),
            thumbAlt: intl.formatMessage(
                { id: 'ListingImageGallery.imageThumbnailAltText' },
                { title, index: i + 1, count: images.length }
            ),
            thumbnail: img.secure_url,
            image: img,
        };
    });

    const renderItem = item => {
        const renderCloudinaryImage = {
            secure_url: item.image.secure_url,
            alt: item.alt,
            variantsToRender: [
                "scaled-small",
                "scaled-medium",
                "scaled-large",
            ],
            defaultVariant: 'scaled-large'
        };

        return (
            <AspectRatioWrapper
                width={aspectWidth || 1}
                height={aspectHeight || 1}
                className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
            >
                <div className={css.itemCentering}>
                    <ResponsiveImage
                        rootClassName={css.item}
                        renderCloudinaryImage={renderCloudinaryImage}
                    />
                </div>
            </AspectRatioWrapper>
        );
    };
    const renderThumbInner = item => {
        const renderCloudinaryImage = {
            secure_url: item.image.secure_url,
            alt: item.alt,
            variantsToRender: [
                "listing-card",
                "listing-card-2x",
            ],
            defaultVariant: 'listing-card-2x',
        }
        return (
            <div>
                <ResponsiveImage
                    rootClassName={css.thumb}
                    renderCloudinaryImage={renderCloudinaryImage}
                />
            </div>
        );
    };

    const onScreenChange = isFull => {
        setIsFullscreen(isFull);
    };

    const onSlide = (index) => {
        setCurrentIndex(index);
    };

    const renderLeftNav = (onClick, disabled) => {
        return (
            <button className={css.navLeft} disabled={disabled} onClick={onClick}>
                <div className={css.navArrowWrapper}>
                    <IconArrowHead direction="left" size="big" />
                </div>
            </button>
        );
    };
    const renderRightNav = (onClick, disabled) => {
        return (
            <button className={css.navRight} disabled={disabled} onClick={onClick}>
                <div className={css.navArrowWrapper}>
                    <IconArrowHead direction="right" size="big" />
                </div>
            </button>
        );
    };
    const renderFullscreenButton = (onClick, isFullscreen) => {
        return isFullscreen ? (
            <Button
                onClick={onClick}
                rootClassName={css.close}
                className={css.close}
                title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
            >
                <span className={css.closeText}>
                    <FormattedMessage id="ListingImageGallery.closeModal" />
                </span>
                <IconClose rootClassName={css.closeIcon} />
            </Button>
        ) : (
            <>
                <div className={css.imageCountContainer}>
                    {`${currentIndex + 1}/${images.length}`}
                </div>
                <button className={css.openFullscreen} onClick={onClick}>
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 16C0 7.16344 7.16344 0 16 0C24.8366 0 32 7.16344 32 16C32 24.8366 24.8366 32 16 32C7.16344 32 0 24.8366 0 16Z" fill="white" />
                        <path d="M19.7239 11.3333H17.3333V10H22V14.6667H20.6667V12.2761L17.8047 15.1381L16.8619 14.1953L19.7239 11.3333ZM10 17.3333H11.3333V19.7239L14.1953 16.8619L15.1381 17.8047L12.2761 20.6667H14.6667V22H10V17.3333Z" fill="#101828" />
                    </svg>
                </button>
            </>
        );
    };

    if (items.length === 0) {
        return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
    }

    const classes = classNames(rootClassName || css.root, className);

    return (
        <ReactImageGallery
            additionalClass={classes}
            items={items}
            renderItem={renderItem}
            renderThumbInner={renderThumbInner}
            onScreenChange={onScreenChange}
            renderLeftNav={renderLeftNav}
            renderRightNav={renderRightNav}
            renderFullscreenButton={renderFullscreenButton}
            onSlide={onSlide}
            {...IMAGE_CAROUSEL_OPTIONS}
        />
    );
};

ImageCarousel.defaultProps = {
    rootClassName: null,
    className: null,
};

ImageCarousel.propTypes = {
    rootClassName: PropTypes.string,
    className: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.object).isRequired,
    title: PropTypes.string.isRequired,
    intl: intlShape.isRequired,
};

export default injectIntl(ImageCarousel);